import React, { useEffect, useRef, useState } from "react";
import ApexCharts from "apexcharts";
import axios from "axios";
import CONSTANTS from "../utils/constants";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

export default function AdminAnalitica() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();

  const [totalSum, setTotalSum] = useState("0.00");
  const [orders, setOrders] = useState(0);
  const [visitors, setVisitors] = useState(0);

  // ✅ Her grafik için ayrı state
  const [priceMonth, setPriceMonth] = useState(new Date().toISOString().slice(0, 7));
  const [viewerMonth, setViewerMonth] = useState(new Date().toISOString().slice(0, 7));

  const getMonthBounds = (date) => ({
    start: new Date(date.getFullYear(), date.getMonth(), 1).toISOString().slice(0, 10),
    end: new Date(date.getFullYear(), date.getMonth() + 1, 0).toISOString().slice(0, 10),
  });
  const initialBounds = getMonthBounds(new Date());
  const [cardStartDate, setCardStartDate] = useState(initialBounds.start);
  const [cardEndDate, setCardEndDate] = useState(initialBounds.end);

  const PRODUCT_PAGE_SIZE = 15;
  const [productNames, setProductNames] = useState([]);
  const [productCounts, setProductCounts] = useState([]);
  const [productPage, setProductPage] = useState(0);

  const [yearlyYear, setYearlyYear] = useState(new Date().getFullYear());
  const [availableYears, setAvailableYears] = useState([new Date().getFullYear()]);

  const priceChartRef = useRef(null);
  const priceChartInstance = useRef(null);
  const productChartRef = useRef(null);
  const productChartInstance = useRef(null);
  const categoryChartRef = useRef(null);
  const categoryChartInstance = useRef(null);
  const viewerChartRef = useRef(null);
  const viewerChartInstance = useRef(null);
  const yearlyChartRef = useRef(null);
  const yearlyChartInstance = useRef(null);

  const getMonthName = (monthString) => {
    const [year, month] = monthString.split("-");
    const months = [
      t("jan"), t("feb"), t("mar"), t("apr"), t("may"), t("jun"),
      t("jul"), t("aug"), t("sep"), t("oct"), t("nov"), t("dec"),
    ];
    return `${months[parseInt(month) - 1]} ${year}`;
  };

  // ✅ Kart verilerini getir (priceMonth'a bağlı)
  const fetchCardData = async () => {
    try {
      const params = { start: cardStartDate, end: cardEndDate };
      const [sumRes, ordersRes, viewerRes] = await Promise.all([
        axios.get(`${CONSTANTS.baseRoute}/analitica/totalSum`, { params }),
        axios.get(`${CONSTANTS.baseRoute}/analitica/orders`, { params }),
        axios.get(`${CONSTANTS.baseRoute}/analitica/viewer`, { params }),
      ]);

      setTotalSum(sumRes.data.total_sum);
      setOrders(ordersRes.data.orders_count);
      setVisitors(viewerRes.data.viewer_count);
    } catch (error) {
      console.error("Kart verileri yüklenirken hata:", error);
    }
  };

  // ✅ Gelir Verilerini Getir
  const fetchPriceData = async () => {
    try {
      const response = await axios.get(`${CONSTANTS.baseRoute}/analitica/price`, {
        params: { month: priceMonth },
      });
      const data = response.data;
      if (!priceChartRef.current) return;

      const [year, monthNum] = priceMonth.split("-").map(Number);
      const daysInMonth = new Date(year, monthNum, 0).getDate();
      const actualData = Array(daysInMonth).fill(0);

      data.forEach((item) => {
        const day = new Date(item.date).getDate() - 1;
        if (day >= 0 && day < daysInMonth) actualData[day] = parseFloat(item.total_price) || 0;
      });

      const options = {
        chart: { type: "area", height: 400, zoom: { enabled: false } },
        series: [{ name: t("jemi_girdeji"), data: actualData }],
        xaxis: { categories: Array.from({ length: daysInMonth }, (_, i) => (i + 1).toString()) },
        yaxis: {
          title: { text: t("aylyk_girdeji_m") },
          labels: { formatter: (val) => `${val.toFixed(0)} m` },
        },
        colors: ["#3B82F6"],
        stroke: { curve: "smooth", width: 3 },
        title: {
          text: `${t("gunluk_girdeji")} (${getMonthName(priceMonth)})`,
          style: { fontSize: "14px", color: "#3494E8" },
        },
      };

      if (priceChartInstance.current) priceChartInstance.current.destroy();
      priceChartInstance.current = new ApexCharts(priceChartRef.current, options);
      priceChartInstance.current.render();
    } catch (error) {
      console.error(error);
    }
  };

  // ✅ İzleyici Verilerini Getir
  const fetchViewerData = async () => {
    try {
      const response = await axios.get(`${CONSTANTS.baseRoute}/analitica/viewer-daily`, {
        params: { month: viewerMonth },
      });

      const data = response.data;
      if (!viewerChartRef.current) return;

      const [year, monthNum] = viewerMonth.split("-").map(Number);
      const daysInMonth = new Date(year, monthNum, 0).getDate();
      const actualData = Array(daysInMonth).fill(0);

      data.forEach((item) => {
        const day = new Date(item.date).getDate() - 1;
        if (day >= 0 && day < daysInMonth) actualData[day] = Number(item.count);
      });

      const options = {
        chart: { type: "area", height: 400, zoom: { enabled: false } },
        series: [{ name: t("ulanyjy"), data: actualData }],
        xaxis: { categories: Array.from({ length: daysInMonth }, (_, i) => (i + 1).toString()) },
        yaxis: {
          title: { text: t("gunluk_ulanyjy") },
          labels: { formatter: (val) => Math.round(val) },
        },
        colors: ["#06B6D4"],
        stroke: { curve: "smooth", width: 3 },
        title: {
          text: `${t("gunluk_ulanyjy")} (${getMonthName(viewerMonth)})`,
          style: { fontSize: "14px", color: "#0891B2" },
        },
      };

      if (viewerChartInstance.current) viewerChartInstance.current.destroy();
      viewerChartInstance.current = new ApexCharts(viewerChartRef.current, options);
      viewerChartInstance.current.render();
    } catch (error) {
      console.error(error);
    }
  };

  // ✅ Ürün Grafiği (Aylık veri - priceMonth'a göre)
  const fetchProductData = async () => {
    try {
      const response = await axios.get(`${CONSTANTS.baseRoute}/analitica/product`, {
        params: { month: priceMonth } // Aylık filtreleme eklendi
      });

      const counts = {};
      response.data.forEach(item => (counts[item.name_tm] = (counts[item.name_tm] || 0) + 1));
      const sortedEntries = Object.entries(counts).sort((a, b) => b[1] - a[1]);
      setProductNames(sortedEntries.map(([name]) => name));
      setProductCounts(sortedEntries.map(([, count]) => count));
      setProductPage(0);
    } catch (error) {
      console.error("Ürün grafiği hatası:", error);
    }
  };

  // ✅ Ürün Grafiğini Sayfaya Göre Çiz
  useEffect(() => {
    if (!productChartRef.current || productNames.length === 0) return;
    const start = productPage * PRODUCT_PAGE_SIZE;
    const end = start + PRODUCT_PAGE_SIZE;
    const options = {
      chart: { type: "bar", height: 400 },
      series: [{ name: t("satylan_haryt"), data: productCounts.slice(start, end) }],
      xaxis: { categories: productNames.slice(start, end) },
      colors: ["#10B981"],
      title: { text: `${t("satylan_harytlar_su_ay")} (${getMonthName(priceMonth)})`, style: { color: "#059669" } },
    };
    if (productChartInstance.current) productChartInstance.current.destroy();
    productChartInstance.current = new ApexCharts(productChartRef.current, options);
    productChartInstance.current.render();
  }, [productNames, productCounts, productPage, i18n.language]);

  // ✅ Kategori Grafiği (Aylık veri - priceMonth'a göre)
  const fetchCategoryData = async () => {
    try {
      const response = await axios.get(`${CONSTANTS.baseRoute}/analitica/category`, {
        params: { month: priceMonth } // Aylık filtreleme eklendi
      });
      
      if (categoryChartRef.current) {
        const counts = {};
        response.data.forEach(item => (counts[item.category_name] = (counts[item.category_name] || 0) + 1));
        const options = {
          chart: { type: "bar", height: 400 },
          series: [{ name: t("satylan_kategoriya"), data: Object.values(counts) }],
          xaxis: { categories: Object.keys(counts) },
          colors: ["#F45142"],
          title: { text: `${t("en_kop_satylan_kategoriyalar")} (${getMonthName(priceMonth)})`, style: { color: "#F45142" } },
        };
        if (categoryChartInstance.current) categoryChartInstance.current.destroy();
        categoryChartInstance.current = new ApexCharts(categoryChartRef.current, options);
        categoryChartInstance.current.render();
      }
    } catch (error) {
      console.error("Kategori grafiği hatası:", error);
    }
  };

  // ✅ Yıllık Girdeji/Sargyt Grafiği
  const fetchYearlyData = async () => {
    try {
      const response = await axios.get(`${CONSTANTS.baseRoute}/analitica/yearly`, {
        params: { year: yearlyYear },
      });
      if (!yearlyChartRef.current) return;

      const monthNames = [
        t("jan"), t("feb"), t("mar"), t("apr"), t("may"), t("jun"),
        t("jul"), t("aug"), t("sep"), t("oct"), t("nov"), t("dec"),
      ];
      const revenueData = response.data.map((row) => parseFloat(row.total_price) || 0);
      const ordersData = response.data.map((row) => row.orders_count || 0);

      const options = {
        chart: { height: 400, type: "line", zoom: { enabled: false } },
        series: [
          { name: t("girdeji"), type: "column", data: revenueData },
          { name: t("sargyt"), type: "line", data: ordersData },
        ],
        xaxis: { categories: monthNames },
        yaxis: [
          {
            title: { text: t("aylyk_girdeji_m") },
            labels: { formatter: (val) => `${val.toFixed(0)} m` },
          },
          {
            opposite: true,
            title: { text: t("sargyt") },
            labels: { formatter: (val) => Math.round(val) },
          },
        ],
        colors: ["#3B82F6", "#F59E0B"],
        stroke: { width: [0, 3], curve: "smooth" },
        title: {
          text: `${t("yyllyk_analiz")} (${yearlyYear})`,
          style: { fontSize: "14px", color: "#3494E8" },
        },
      };

      if (yearlyChartInstance.current) yearlyChartInstance.current.destroy();
      yearlyChartInstance.current = new ApexCharts(yearlyChartRef.current, options);
      yearlyChartInstance.current.render();
    } catch (error) {
      console.error("Yıllık grafik hatası:", error);
    }
  };

  // ✅ Yıl listesini getir
  useEffect(() => {
    const fetchYears = async () => {
      try {
        const response = await axios.get(`${CONSTANTS.baseRoute}/analitica/years`);
        if (Array.isArray(response.data) && response.data.length > 0) {
          setAvailableYears(response.data);
        }
      } catch (error) {
        console.error("Yıl listesi hatası:", error);
      }
    };
    fetchYears();
  }, []);

  // ✅ Yıllık grafiği güncelle (yearlyYear değişince)
  useEffect(() => {
    fetchYearlyData();
  }, [yearlyYear, i18n.language]);

  // ✅ Tüm verileri güncelle (priceMonth değişince)
  useEffect(() => {
    fetchPriceData();
    fetchProductData();
    fetchCategoryData();
  }, [priceMonth, i18n.language]);

  // ✅ Kartlar (tarih aralığı değişince)
  useEffect(() => {
    fetchCardData();
  }, [cardStartDate, cardEndDate]);

  // ✅ Viewer grafiği (viewerMonth değişince)
  useEffect(() => {
    fetchViewerData();
  }, [viewerMonth, i18n.language]);

  // ✅ İlk yükleme
  useEffect(() => {
    const currentMonth = new Date().toISOString().slice(0, 7);
    setPriceMonth(currentMonth);
    setViewerMonth(currentMonth);
  }, []);

  return (
    <div className="admin-main p-2 overflow-x-hidden">
      {/* Üst Kısım Navigasyon */}
      <div className="d-flex gap-2 align-items-center mb-2">
        <button className="btn btn-light" onClick={() => navigate(-1)}><i className="bi bi-chevron-left"></i></button>
        <button className="btn btn-light" onClick={() => navigate(+1)}><i className="bi bi-chevron-right"></i></button>
        <h5 className=" mb-0 fw-bold">{t("analitika")}</h5>
      </div>

      {/* Kartlar - priceMonth'a bağlı */}
      <div className="card border bg-light p-2 mb-3">
        <div className="d-flex flex-wrap justify-content-between align-items-center mb-4 gap-2">
          <h5 className="mb-0 fw-bold">{t("sul_ay")}</h5>
          <div className="d-flex align-items-center gap-2">
            <input
              type="date"
              className="form-control form-control-sm"
              value={cardStartDate}
              max={cardEndDate}
              onChange={(e) => setCardStartDate(e.target.value)}
              style={{ width: "150px" }}
            />
            <span className="text-muted">-</span>
            <input
              type="date"
              className="form-control form-control-sm"
              value={cardEndDate}
              min={cardStartDate}
              onChange={(e) => setCardEndDate(e.target.value)}
              style={{ width: "150px" }}
            />
          </div>
        </div>
        <div className="row row-cols-1 row-cols-md-3 g-3 align-items-end">
          <div className="col">
            <div className="border shadow-sm rounded-2 p-3 bg-white">
              <h6 className="text-muted mb-0">{t("girdeji")}</h6>
              <h3 className="fw-bold mt-2 mb-0">{totalSum} M</h3>
            </div>
          </div>
          <div className="col">
            <div className="border shadow-sm rounded-2 p-3 bg-white">
              <h6 className="text-muted mb-0">{t("sargyt")}</h6>
              <h3 className="fw-bold mt-2 mb-0">{orders}</h3>
            </div>
          </div>
          <div className="col">
            <div className="border shadow-sm rounded-2 p-3 bg-white">
              <h6 className="text-muted mb-0">{t("ulanyjy")}</h6>
              <h3 className="fw-bold mt-2 mb-0">{visitors}</h3>
            </div>
          </div>
        </div>
      </div>

      <div className="border shadow-sm rounded-2 p-3 mb-4">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h6 className="mb-0 fw-bold">{t("gunluk_girdeji")}</h6>
          <div className="d-flex align-items-center gap-2">
            <label className="form-label mb-0 small text-muted">{t("ay_saylan")}:</label>
            <input
              type="month"
              className="form-control form-control-sm"
              value={priceMonth}
              onChange={(e) => setPriceMonth(e.target.value)}
              style={{ width: "150px" }}
            />
          </div>
        </div>
        <div ref={priceChartRef} style={{ minHeight: "400px" }}></div>
      </div>

      <div className="border shadow-sm rounded-2 p-3 mb-4">
        <div ref={productChartRef} style={{ minHeight: "400px" }}></div>
        {productNames.length > PRODUCT_PAGE_SIZE && (
          <div className="d-flex justify-content-center align-items-center gap-3 mt-2">
            <button
              className="btn btn-sm btn-outline-secondary"
              disabled={productPage === 0}
              onClick={() => setProductPage((p) => Math.max(0, p - 1))}
            >
              <i className="bi bi-chevron-left"></i>
            </button>
            <span className="small text-muted">
              {productPage + 1} / {Math.ceil(productNames.length / PRODUCT_PAGE_SIZE)}
            </span>
            <button
              className="btn btn-sm btn-outline-secondary"
              disabled={(productPage + 1) * PRODUCT_PAGE_SIZE >= productNames.length}
              onClick={() =>
                setProductPage((p) =>
                  (p + 1) * PRODUCT_PAGE_SIZE < productNames.length ? p + 1 : p
                )
              }
            >
              <i className="bi bi-chevron-right"></i>
            </button>
          </div>
        )}
      </div>

      <div className="border shadow-sm rounded-2 p-3 mb-4">
        <div ref={categoryChartRef} style={{ minHeight: "400px" }}></div>
      </div>

      <div className="border shadow-sm rounded-2 p-3 mb-2">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h6 className="mb-0 fw-bold">{t("gunluk_ulanyjy")}</h6>
          <div className="d-flex align-items-center gap-2">
            <label className="form-label mb-0 small text-muted">{t("ay_saylan")}:</label>
            <input
              type="month"
              className="form-control form-control-sm"
              value={viewerMonth}
              onChange={(e) => setViewerMonth(e.target.value)}
              style={{ width: "150px" }}
            />
          </div>
        </div>
        <div ref={viewerChartRef} style={{ minHeight: "400px" }}></div>
      </div>

      <div className="border shadow-sm rounded-2 p-3 mb-2">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h6 className="mb-0 fw-bold">{t("yyllyk_analiz")}</h6>
          <div className="d-flex align-items-center gap-2">
            <label className="form-label mb-0 small text-muted">{t("yil_saylan")}:</label>
            <select
              className="form-select form-select-sm"
              value={yearlyYear}
              onChange={(e) => setYearlyYear(Number(e.target.value))}
              style={{ width: "120px" }}
            >
              {availableYears.map((y) => (
                <option key={y} value={y}>
                  {y}
                </option>
              ))}
            </select>
          </div>
        </div>
        <div ref={yearlyChartRef} style={{ minHeight: "400px" }}></div>
      </div>
    </div>
  );
}